/**
 * /admin/rule-suggestions — Rule Suggestions Review Queue.
 *
 * A FAST triage surface for the HUNDREDS of corpus-mined scope-rule
 * suggestions. Hybrid UX: a dense TanStack-Table queue with inline controls
 * (per-row severity select + approve/reject/edit), bulk-select + bulk actions,
 * and keyboard navigation (j/k/space/a/x/e). Clicking a row opens a right-side
 * drawer that reuses RuleEditForm for full condition/action JSON editing plus
 * the verbatim corpus citation.
 *
 * Visible only to users with an editing role (matches /admin/rules gating).
 */

import * as React from 'react';
import { Navigate } from 'react-router-dom';
import {
  Check,
  Pencil,
  ShieldAlert,
  X,
} from 'lucide-react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
} from '@tanstack/react-table';

import useAuthStore from '../stores/authStore';
import { RuleSuggestionsAPI } from '../lib/ruleSuggestionsApi';
import { cn, getErrorMessage } from '../lib/utils';
import { RuleTypeBadge } from '../components/rules/RuleBadges';
import RuleEditForm from '../components/rules/RuleEditForm';
import type {
  BulkActionVerb,
  SuggestionRow,
  SuggestionSource,
  SuggestionStats,
} from '../types/ruleSuggestions';
import type {
  RuleDetail as RuleDetailType,
  RuleSeverity,
  RuleStatus,
  RuleType,
} from '../types/rules';

// Matches how /admin/rules is gated (editor/expert/admin/super_admin) plus the
// expert role per the page spec.
const EDIT_ROLES = ['editor', 'expert', 'admin', 'super_admin'];

const PAGE_SIZE = 100;

// Row density: a one-line headline for fast scanning vs. name + description so
// the queue can be read by scrolling instead of opening each row. Persisted.
type ViewMode = 'compact' | 'detailed';
const VIEW_MODE_KEY = 'ruleSuggestions.viewMode';

const SEVERITY_OPTIONS: RuleSeverity[] = ['must', 'should', 'suggested'];

const SOURCE_LABELS: Record<SuggestionSource, string> = {
  f9_note: 'F9 note',
  co_occurrence: 'corpus',
  quantity: 'quantity',
  room_template: 'room tmpl',
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** Confidence as `92%` when numeric, else High/Med/Low buckets, else `—`. */
function confidenceLabel(confidence: number | null): string {
  if (confidence === null || Number.isNaN(confidence)) return '—';
  return `${Math.round(confidence * 100)}%`;
}

function confidenceBucket(confidence: number | null): 'High' | 'Med' | 'Low' | null {
  if (confidence === null || Number.isNaN(confidence)) return null;
  if (confidence >= 0.8) return 'High';
  if (confidence >= 0.5) return 'Med';
  return 'Low';
}

/** Map a queue row to a RuleDetail-shaped object the edit form can consume. */
function suggestionToRuleDetail(row: SuggestionRow): RuleDetailType {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    category: row.source_corpus,
    priority: 0,
    is_active: false,
    rule_type: (row.rule_type as RuleType) ?? 'companion',
    status: (row.status as RuleStatus) ?? 'proposed',
    severity: row.severity,
    domains: [],
    tags: row.tags ?? [],
    citations: row.citation
      ? [
          {
            source_type: SOURCE_LABELS[row.source_corpus] ?? row.source_corpus,
            reference: row.citation.reference,
            summary: row.citation.summary,
          },
        ]
      : [],
    plain_english_render: row.plain_english_render,
    conditions: row.conditions ?? {},
    actions: row.actions ?? [],
    produces_facts: [],
    jurisdictions: [],
    damage_types: [],
    version: 1,
  };
}

// ---------------------------------------------------------------------------
// Source / confidence badges
// ---------------------------------------------------------------------------

const SourceBadge: React.FC<{ source: SuggestionSource }> = ({ source }) => (
  <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:text-muted-foreground">
    {SOURCE_LABELS[source] ?? source}
  </span>
);

const ConfidenceBadge: React.FC<{ confidence: number | null; support: number | null }> = ({
  confidence,
  support,
}) => {
  const bucket = confidenceBucket(confidence);
  const tone =
    bucket === 'High'
      ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30'
      : bucket === 'Low'
        ? 'bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-500/30'
        : 'bg-slate-50 dark:bg-muted text-slate-600 dark:text-muted-foreground border-border';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium',
        tone,
      )}
      title={support != null ? `support: ${support}` : undefined}
    >
      {confidenceLabel(confidence)}
      {support != null && <span className="text-slate-400 dark:text-slate-500">·{support}</span>}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

interface SuggestionDrawerProps {
  row: SuggestionRow | null;
  onClose: () => void;
  /** Called after a successful save so the parent can refetch. */
  onSaved: () => void;
}

const SuggestionDrawer: React.FC<SuggestionDrawerProps> = ({ row, onClose, onSaved }) => {
  // Close on Escape for keyboard users.
  React.useEffect(() => {
    if (!row) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [row, onClose]);

  if (!row) return null;
  const detail = suggestionToRuleDetail(row);

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-label={`Suggestion detail: ${row.name}`}
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b bg-card p-5">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <RuleTypeBadge ruleType={detail.rule_type} />
              <SourceBadge source={row.source_corpus} />
              <ConfidenceBadge confidence={row.confidence} support={row.support} />
            </div>
            <h2 className="text-lg font-semibold text-foreground">{row.headline || row.name}</h2>
            {row.description && <p className="text-sm text-slate-600 dark:text-muted-foreground">{row.description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-slate-500 dark:text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close suggestion detail"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="space-y-6 p-5">
          {/* Verbatim corpus citation */}
          {row.citation && (
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Citation
              </h3>
              <div className="rounded border border-border bg-slate-50 dark:bg-muted p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-slate-600 dark:text-muted-foreground">
                  <span className="font-semibold">{row.citation.reference}</span>
                </div>
                {row.citation.summary && (
                  <p className="mt-1 text-sm text-muted-foreground">{row.citation.summary}</p>
                )}
                {row.citation.applicable_text && (
                  <blockquote className="mt-2 border-l-2 border-slate-300 dark:border-border pl-3 text-sm italic text-slate-600 dark:text-muted-foreground">
                    {row.citation.applicable_text}
                  </blockquote>
                )}
              </div>
            </section>
          )}

          {/* Full condition/action JSON edit (reuses the rules edit form,
              which PUTs /rules/{id} via the existing rules API client). */}
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Edit rule
            </h3>
            <RuleEditForm
              rule={detail}
              onCancel={onClose}
              onSaved={() => {
                onSaved();
              }}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// RuleSuggestionsPage
// ---------------------------------------------------------------------------

export default function RuleSuggestionsPage(): React.ReactElement {
  const { roles } = useAuthStore();
  const hasAccess = EDIT_ROLES.some((r) => roles.includes(r));

  // Filters
  const [sourceFilter, setSourceFilter] = React.useState('');
  const [minConfidence, setMinConfidence] = React.useState('');
  const [ruleTypeFilter, setRuleTypeFilter] = React.useState('');
  const [severityFilter, setSeverityFilter] = React.useState('');
  const [search, setSearch] = React.useState('');

  // Data
  const [rows, setRows] = React.useState<SuggestionRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [stats, setStats] = React.useState<SuggestionStats | null>(null);
  const [offset, setOffset] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Selection + keyboard focus
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [busy, setBusy] = React.useState(false);

  // Drawer
  const [drawerRow, setDrawerRow] = React.useState<SuggestionRow | null>(null);

  // View density — compact one-liner vs. detailed name+description (persisted).
  const [viewMode, setViewMode] = React.useState<ViewMode>(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem(VIEW_MODE_KEY) : null;
      return saved === 'compact' || saved === 'detailed' ? saved : 'detailed';
    } catch {
      return 'detailed';
    }
  });
  React.useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch {
      /* ignore persistence failures (private mode, etc.) */
    }
  }, [viewMode]);

  const listParams = React.useMemo(
    () => ({
      source_corpus: sourceFilter || undefined,
      min_confidence: minConfidence ? Number(minConfidence) : undefined,
      rule_type: ruleTypeFilter || undefined,
      severity: severityFilter || undefined,
      search: search.trim() || undefined,
      status: 'proposed',
      limit: PAGE_SIZE,
    }),
    [sourceFilter, minConfidence, ruleTypeFilter, severityFilter, search],
  );

  const refetchStats = React.useCallback(() => {
    RuleSuggestionsAPI.stats()
      .then(setStats)
      .catch(() => {
        /* stats are a non-fatal enhancement; ignore */
      });
  }, []);

  // Fetch the queue. `append` keeps existing rows for "load more".
  const fetchPage = React.useCallback(
    (nextOffset: number, append: boolean) => {
      if (!hasAccess) return;
      setLoading(true);
      setError(null);
      RuleSuggestionsAPI.list({ ...listParams, offset: nextOffset })
        .then((res) => {
          setTotal(res.total);
          setRows((prev) => (append ? [...prev, ...res.items] : res.items));
        })
        .catch((err) => setError(getErrorMessage(err)))
        .finally(() => setLoading(false));
    },
    [hasAccess, listParams],
  );

  // Reset + reload whenever filters change.
  React.useEffect(() => {
    if (!hasAccess) return;
    setOffset(0);
    setRowSelection({});
    setActiveIndex(0);
    fetchPage(0, false);
    refetchStats();
  }, [hasAccess, fetchPage, refetchStats]);

  // Drop rows from the queue after they're approved/rejected. Optimistic.
  const removeRows = React.useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    setRows((prev) => prev.filter((r) => !idSet.has(r.id)));
    setRowSelection((prev) => {
      const next: RowSelectionState = {};
      for (const [k, v] of Object.entries(prev)) {
        if (!idSet.has(k)) next[k] = v;
      }
      return next;
    });
    setTotal((t) => Math.max(0, t - ids.length));
  }, []);

  // Apply a bulk action against the API, then update local state + stats.
  const applyBulk = React.useCallback(
    async (ids: string[], action: BulkActionVerb, severity?: RuleSeverity) => {
      if (ids.length === 0 || busy) return;
      setBusy(true);
      setError(null);
      try {
        await RuleSuggestionsAPI.bulk({ ids, action, severity });
        if (action === 'set_level' && severity) {
          // Keep the row in the queue, just reflect the new level.
          setRows((prev) =>
            prev.map((r) => (ids.includes(r.id) ? { ...r, severity } : r)),
          );
        } else {
          // approve / reject — drop from the queue.
          removeRows(ids);
        }
        refetchStats();
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [busy, removeRows, refetchStats],
  );

  const selectedIds = React.useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  // ----- Table -----------------------------------------------------------
  const columns = React.useMemo<ColumnDef<SuggestionRow, unknown>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            aria-label="Select all"
            className="h-4 w-4"
            checked={table.getIsAllRowsSelected()}
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomeRowsSelected();
            }}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label={`Select ${row.original.name}`}
            className="h-4 w-4"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        id: 'headline',
        header: 'Suggestion',
        cell: ({ row }) => {
          const r = row.original;
          if (viewMode === 'compact') {
            return (
              <div className="min-w-0">
                <span className="block truncate font-medium text-foreground">
                  {r.headline || r.name}
                </span>
              </div>
            );
          }
          // Detailed: name with the human-readable description (or citation /
          // plain-english fallback) below, clamped so rows stay scannable.
          const detail = r.description || r.citation?.summary || r.plain_english_render;
          return (
            <div className="min-w-0 max-w-prose space-y-1 py-0.5">
              <span className="block font-medium text-foreground">{r.headline || r.name}</span>
              {detail && (
                <p className="line-clamp-3 text-xs leading-snug text-slate-500 dark:text-muted-foreground">{detail}</p>
              )}
            </div>
          );
        },
      },
      {
        id: 'severity',
        header: 'Level',
        cell: ({ row }) => (
          <select
            aria-label={`Severity for ${row.original.name}`}
            value={row.original.severity}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              void applyBulk([row.original.id], 'set_level', e.target.value as RuleSeverity);
            }}
            className="rounded border border-slate-300 dark:border-border px-1.5 py-1 text-xs"
          >
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        ),
      },
      {
        id: 'confidence',
        header: 'Conf.',
        cell: ({ row }) => (
          <ConfidenceBadge confidence={row.original.confidence} support={row.original.support} />
        ),
      },
      {
        id: 'source',
        header: 'Source',
        cell: ({ row }) => <SourceBadge source={row.original.source_corpus} />,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              aria-label={`Approve ${row.original.name}`}
              title="Approve"
              onClick={(e) => {
                e.stopPropagation();
                void applyBulk([row.original.id], 'approve');
              }}
              className="rounded border border-emerald-300 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/15 p-1 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={`Reject ${row.original.name}`}
              title="Reject"
              onClick={(e) => {
                e.stopPropagation();
                void applyBulk([row.original.id], 'reject');
              }}
              className="rounded border border-red-300 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-1 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={`Edit ${row.original.name}`}
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setDrawerRow(row.original);
              }}
              className="rounded border border-slate-300 dark:border-border bg-card p-1 text-slate-600 dark:text-muted-foreground hover:bg-slate-50 dark:hover:bg-muted"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    [applyBulk, viewMode],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { rowSelection },
    getRowId: (row) => row.id,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  });

  // Keep the active index inside the current bounds.
  React.useEffect(() => {
    if (activeIndex >= rows.length) {
      setActiveIndex(Math.max(0, rows.length - 1));
    }
  }, [rows.length, activeIndex]);

  // ----- Keyboard navigation --------------------------------------------
  React.useEffect(() => {
    if (!hasAccess || drawerRow) return; // drawer handles its own keys
    const handler = (e: KeyboardEvent) => {
      // Ignore typing in inputs / selects / textareas.
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (rows.length === 0) return;

      const active = rows[activeIndex];
      switch (e.key) {
        case 'j':
          e.preventDefault();
          setActiveIndex((i) => Math.min(rows.length - 1, i + 1));
          break;
        case 'k':
          e.preventDefault();
          setActiveIndex((i) => Math.max(0, i - 1));
          break;
        case ' ':
          e.preventDefault();
          if (active) {
            setRowSelection((prev) => ({ ...prev, [active.id]: !prev[active.id] }));
          }
          break;
        case 'a':
          e.preventDefault();
          if (active) void applyBulk([active.id], 'approve');
          break;
        case 'x':
          e.preventDefault();
          if (active) void applyBulk([active.id], 'reject');
          break;
        case 'e':
          e.preventDefault();
          if (active) setDrawerRow(active);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hasAccess, drawerRow, rows, activeIndex, applyBulk]);

  if (!hasAccess) {
    return (
      <div className="mx-auto max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
        <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
        <h1 className="mt-3 text-lg font-semibold">Editor access required</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The Rule Suggestions queue is only accessible to rule editors. Contact
          an admin if you believe you should have access.
        </p>
        <Navigate to="/" replace />
      </div>
    );
  }

  const selectInputClass =
    'rounded border border-slate-300 dark:border-border bg-card px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className="space-y-4">
      {/* Header + pending count */}
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">Rule Suggestions</h1>
        <div className="flex items-center gap-3">
          {/* Compact ↔ Detailed row view */}
          <div
            role="group"
            aria-label="Row view density"
            className="inline-flex overflow-hidden rounded border border-slate-300 dark:border-border text-xs"
          >
            <button
              type="button"
              aria-pressed={viewMode === 'compact'}
              onClick={() => setViewMode('compact')}
              className={cn(
                'px-2.5 py-1.5 font-medium',
                viewMode === 'compact'
                  ? 'bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900'
                  : 'bg-card text-slate-600 dark:text-muted-foreground hover:bg-slate-50 dark:hover:bg-muted',
              )}
            >
              Compact
            </button>
            <button
              type="button"
              aria-pressed={viewMode === 'detailed'}
              onClick={() => setViewMode('detailed')}
              className={cn(
                'border-l border-slate-300 dark:border-border px-2.5 py-1.5 font-medium',
                viewMode === 'detailed'
                  ? 'bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900'
                  : 'bg-card text-slate-600 dark:text-muted-foreground hover:bg-slate-50 dark:hover:bg-muted',
              )}
            >
              Detailed
            </button>
          </div>
          <span className="text-sm text-muted-foreground">
            {stats ? `${stats.total} pending` : `${total} pending`}
          </span>
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3 shadow-sm">
        <select
          aria-label="Source"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className={selectInputClass}
        >
          <option value="">All sources</option>
          <option value="f9_note">F9 note</option>
          <option value="co_occurrence">Co-occurrence</option>
          <option value="quantity">Quantity</option>
          <option value="room_template">Room template</option>
        </select>

        <label className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-muted-foreground">
          Confidence ≥
          <input
            type="number"
            min={0}
            max={1}
            step={0.05}
            aria-label="Minimum confidence"
            value={minConfidence}
            onChange={(e) => setMinConfidence(e.target.value)}
            className="w-20 rounded border border-slate-300 dark:border-border px-2 py-1.5 text-sm"
          />
        </label>

        <select
          aria-label="Type"
          value={ruleTypeFilter}
          onChange={(e) => setRuleTypeFilter(e.target.value)}
          className={selectInputClass}
        >
          <option value="">All types</option>
          <option value="trigger">Trigger</option>
          <option value="activity_resolution">Activity Resolution</option>
          <option value="validation">Validation</option>
          <option value="quantity_heuristic">Quantity Heuristic</option>
          <option value="companion">Companion</option>
        </select>

        <select
          aria-label="Level"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className={selectInputClass}
        >
          <option value="">All levels</option>
          <option value="must">must</option>
          <option value="should">should</option>
          <option value="suggested">suggested</option>
        </select>

        <input
          type="search"
          aria-label="Search suggestions"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-[12rem] flex-1 rounded border border-slate-300 dark:border-border px-2 py-1.5 text-sm"
        />
      </div>

      {error && (
        <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Bulk action bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-slate-50/80 dark:bg-muted p-3">
        <button
          type="button"
          disabled={selectedIds.length === 0 || busy}
          onClick={() => void applyBulk(selectedIds, 'approve')}
          className="inline-flex items-center gap-1.5 rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" />
          Approve selected ({selectedIds.length})
        </button>
        <button
          type="button"
          disabled={selectedIds.length === 0 || busy}
          onClick={() => void applyBulk(selectedIds, 'reject')}
          className="inline-flex items-center gap-1.5 rounded border border-red-300 dark:border-red-500/30 bg-card px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
          Reject selected
        </button>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-muted-foreground">
          Set level
          <select
            aria-label="Set level for selected"
            disabled={selectedIds.length === 0 || busy}
            value=""
            onChange={(e) => {
              if (e.target.value) {
                void applyBulk(selectedIds, 'set_level', e.target.value as RuleSeverity);
                e.target.value = '';
              }
            }}
            className="rounded border border-slate-300 dark:border-border px-1.5 py-1 text-xs disabled:opacity-50"
          >
            <option value="">—</option>
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <span className="ml-auto text-[11px] text-slate-400 dark:text-slate-500">
          j/k move · a approve · x reject · e edit · space select
        </span>
      </div>

      {/* Dense queue table */}
      <div className="overflow-auto rounded-lg border bg-card shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    className="sticky top-0 z-[1] border-b bg-slate-50 dark:bg-muted px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-muted-foreground"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, i) => (
              <tr
                key={row.id}
                onClick={() => setDrawerRow(row.original)}
                className={cn(
                  'cursor-pointer border-b transition-colors hover:bg-slate-50 dark:hover:bg-muted',
                  i === activeIndex && 'bg-primary/5 ring-1 ring-inset ring-primary/30',
                  row.getIsSelected() && 'bg-blue-50/50 dark:bg-blue-500/15',
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      'px-3 py-2',
                      viewMode === 'detailed' ? 'align-top' : 'align-middle',
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && rows.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No pending suggestions match these filters.
          </div>
        )}

        {loading && rows.length === 0 && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-muted" />
            ))}
          </div>
        )}
      </div>

      {/* Load more */}
      {rows.length < total && (
        <div className="flex justify-center">
          <button
            type="button"
            disabled={loading}
            onClick={() => {
              const next = offset + PAGE_SIZE;
              setOffset(next);
              fetchPage(next, true);
            }}
            className="rounded border border-slate-300 dark:border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-slate-50 dark:hover:bg-muted disabled:opacity-50"
          >
            {loading ? 'Loading…' : `Load more (${rows.length} of ${total})`}
          </button>
        </div>
      )}

      {/* Edit drawer */}
      <SuggestionDrawer
        row={drawerRow}
        onClose={() => setDrawerRow(null)}
        onSaved={() => {
          setDrawerRow(null);
          fetchPage(0, false);
          refetchStats();
        }}
      />
    </div>
  );
}
